/**
 * Order Status Utilities
 * 
 * Maps technical order statuses to user-friendly display names and colors.
 * Based on the standardizedStatus system from the API.
 */

// All known technical statuses mapped to simplified display statuses
type SimplifiedStatus = 'Pending' | 'Action Required' | 'Processing' | 'Shipped' | 'Delivered' | 'Cancelled';

// Badge variant types from the UI component
type BadgeVariant = 'success' | 'warning' | 'primary' | 'accent' | 'danger' | 'secondary';

/**
 * Maps raw technical status strings to user-friendly display names
 */
const STATUS_DISPLAY_MAP: Record<string, SimplifiedStatus> = {
  // Pending statuses
  'pending': 'Pending',
  'PENDING': 'Pending',
  'order_placed': 'Pending',
  'ORDER_PLACED': 'Pending',
  'payment_pending': 'Pending',
  'PAYMENT_PENDING': 'Pending',
  'awaiting_payment': 'Pending',
  'AWAITING_PAYMENT': 'Pending',
  
  // Action Required statuses
  'requires_action': 'Action Required',
  'REQUIRES_ACTION': 'Action Required',
  'requires-action': 'Action Required',
  'driver_license_requested': 'Action Required',
  'DRIVER_LICENSE_REQUESTED': 'Action Required',
  'file_upload_requested': 'Action Required',
  'FILE_UPLOAD_REQUESTED': 'Action Required',
  'questionnaire_pending': 'Action Required',
  'QUESTIONNAIRE_PENDING': 'Action Required',
  'questionnaire_incomplete': 'Action Required',
  'QUESTIONNAIRE_INCOMPLETE': 'Action Required',
  'identity_verification_required': 'Action Required',
  'IDENTITY_VERIFICATION_REQUIRED': 'Action Required',
  'photo_id_required': 'Action Required',
  'PHOTO_ID_REQUIRED': 'Action Required',
  'additional_info_needed': 'Action Required',
  'ADDITIONAL_INFO_NEEDED': 'Action Required',
  'provider_review_pending': 'Action Required',
  'PROVIDER_REVIEW_PENDING': 'Action Required',
  
  // Processing statuses
  'processing': 'Processing',
  'PROCESSING': 'Processing',
  'order_in_progress': 'Processing',
  'ORDER_IN_PROGRESS': 'Processing',
  'mdi_processing': 'Processing',
  'MDI_PROCESSING': 'Processing',
  'curexa_received': 'Processing',
  'CUREXA_RECEIVED': 'Processing',
  'pharmacy_processing': 'Processing',
  'PHARMACY_PROCESSING': 'Processing',
  'fulfillment_pending': 'Processing',
  'FULFILLMENT_PENDING': 'Processing',
  'prescription_sent': 'Processing',
  'PRESCRIPTION_SENT': 'Processing',
  'approved': 'Processing',
  'APPROVED': 'Processing',
  
  // Shipped statuses
  'shipped': 'Shipped',
  'SHIPPED': 'Shipped',
  'in_transit': 'Shipped',
  'IN_TRANSIT': 'Shipped',
  'out_for_delivery': 'Shipped',
  'OUT_FOR_DELIVERY': 'Shipped',
  
  // Delivered statuses
  'delivered': 'Delivered',
  'DELIVERED': 'Delivered',
  'completed': 'Delivered',
  'COMPLETED': 'Delivered',
  
  // Cancelled statuses
  'cancelled': 'Cancelled',
  'CANCELLED': 'Cancelled',
  'canceled': 'Cancelled',
  'CANCELED': 'Cancelled',
  'refunded': 'Cancelled',
  'REFUNDED': 'Cancelled',
  'voided': 'Cancelled',
  'VOIDED': 'Cancelled',
  'denied': 'Cancelled',
  'DENIED': 'Cancelled',
  'rejected': 'Cancelled',
  'REJECTED': 'Cancelled',
};

/**
 * Maps simplified status to badge color variant
 */
const STATUS_VARIANT_MAP: Record<SimplifiedStatus, BadgeVariant> = {
  'Pending': 'secondary',      // Gray/neutral
  'Action Required': 'warning', // Yellow/orange
  'Processing': 'primary',      // Blue
  'Shipped': 'accent',          // Purple
  'Delivered': 'success',       // Green
  'Cancelled': 'danger',        // Red
};

/**
 * Get the user-friendly display name for a status
 * @param status - The raw technical status string
 * @returns The simplified, user-friendly status name
 */
export const getStatusDisplayName = (status: string | null | undefined): SimplifiedStatus => {
  if (!status) return 'Pending';
  
  // First try direct lookup
  const mapped = STATUS_DISPLAY_MAP[status];
  if (mapped) return mapped;
  
  // Try normalized version (lowercase with underscores)
  const normalized = status.toLowerCase().replace(/-/g, '_');
  const normalizedMapped = STATUS_DISPLAY_MAP[normalized];
  if (normalizedMapped) return normalizedMapped;
  
  // Default to Processing for unknown statuses (safe middle ground)
  return 'Processing';
};

/**
 * Get the badge variant (color) for a status
 * @param status - The raw technical status string OR already simplified status
 * @returns The badge variant for styling
 */
export const getStatusBadgeVariant = (status: string | null | undefined): BadgeVariant => {
  if (!status) return 'secondary';
  
  // Check if it's already a simplified status
  if (status in STATUS_VARIANT_MAP) {
    return STATUS_VARIANT_MAP[status as SimplifiedStatus];
  }
  
  // Get the display name first, then the variant
  const displayName = getStatusDisplayName(status);
  return STATUS_VARIANT_MAP[displayName];
};

/**
 * Combined helper that returns both display name and variant
 * @param status - The raw technical status string
 * @returns Object with displayName and variant
 */
export const getStatusInfo = (status: string | null | undefined): { displayName: SimplifiedStatus; variant: BadgeVariant } => {
  const displayName = getStatusDisplayName(status);
  const variant = STATUS_VARIANT_MAP[displayName];
  return { displayName, variant };
};

/**
 * Check if a status requires user action
 * @param status - The raw technical status string
 * @returns true if the status indicates action is required
 */
export const isActionRequired = (status: string | null | undefined): boolean => {
  return getStatusDisplayName(status) === 'Action Required';
};

/**
 * Check if a status is in a terminal state (completed or cancelled)
 * @param status - The raw technical status string
 * @returns true if the order is in a final state
 */
export const isTerminalStatus = (status: string | null | undefined): boolean => {
  const displayName = getStatusDisplayName(status);
  return displayName === 'Delivered' || displayName === 'Cancelled';
};
